import { isNativeAppPage } from './nativeStatusBar';

// The native store apps (mobileApp/brands/*, Cordova wrappers) open the hosted web app with
// ?platform=ios|android&nativeApp=<brand>; template-prod.html then loads cordova.js, which
// defines window.cordova. Hash routing keeps the query string, so either signal marks the
// whole session as "inside the app".
export function isNativeApp(win = typeof window !== 'undefined' ? window : undefined) {
    return isNativeAppPage(win) || !!win?.cordova;
}

// Custom URL scheme of the running store app, or null. The wrapper passes ?nativeScheme=<scheme>
// (mobileApp/brands/*/app.json → urlScheme) only in builds that registered the scheme in
// Info.plist / AndroidManifest, so a non-null value means the Vipps app can return the user
// with <scheme>://vipps-return/<paymentUuid>. Same regex as mobileApp/scripts/lib/mobile-app.js.
// Reserved web schemes are refused even if a wrapper ever sent them by mistake.
const NATIVE_SCHEME_PATTERN = /^[a-z][a-z0-9+.-]{2,}$/;
const NATIVE_SCHEME_BLOCKLIST = ['http', 'https', 'file', 'mailto', 'tel', 'sms', 'geo', 'vipps', 'vippsmt', 'itms', 'itms-apps'];

export function getNativeReturnScheme(win = typeof window !== 'undefined' ? window : undefined) {
    if (!isNativeAppPage(win)) {
        return null;
    }
    const scheme = new URLSearchParams(win.location.search).get('nativeScheme');
    if (!scheme || !NATIVE_SCHEME_PATTERN.test(scheme) || NATIVE_SCHEME_BLOCKLIST.includes(scheme)) {
        return null;
    }
    return scheme;
}

// Whether the running store app can complete a passkey ceremony. WKWebView only runs
// navigator.credentials.* for a domain the binary is associated with (webcredentials
// entitlement), and Android WebView has no WebAuthn at all — neither is detectable from
// JS, so the wrapper declares it: iOS builds that carry the entitlement send
// ?nativePasskeys=1 (mobileApp/www/js/index.js). Older builds and Android never do.
export function hasNativePasskeys(win = typeof window !== 'undefined' ? window : undefined) {
    if (!isNativeAppPage(win)) {
        return false;
    }
    const search = new URLSearchParams(win.location.search);
    return search.get('platform') === 'ios' && search.get('nativePasskeys') === '1';
}

// Last route inside the store apps.
//
// iOS suspends a backgrounded app and, after some hours or under memory pressure, kills it.
// The next tap is a cold launch: the Cordova shell (mobileApp/www/js/index.js) redirects to
// /index?platform=... with no hash, so the hosted app booted on the frontpage and every
// store reloaded. The shell itself cannot remember the route — it runs on a file:// origin
// with its own localStorage — so the hosted app records its route on every navigation
// (app.js handleRoute) and the boot path routes back to it when it starts without a hash
// (app.js componentDidMount). A present hash always wins, so deep links are untouched.
// When only WebKit's content process dies, cordova-ios reloads the current URL and the hash
// survives, so this only changes the cold-launch case.
export const NATIVE_LAST_ROUTE_KEY = 'nativeLastRoute';
export const NATIVE_LAST_ROUTE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
// Auth and gating routes are decided by redirectIfNotLoggedIn / redirectIfNotApproved at
// boot; restoring them would either loop or show a login page to a logged-in user. The
// frontpage is where a hash-less start lands anyway.
const NON_RESTORABLE_ROUTE = /^\/(welcome|login|register|forgotten|waiting-approval)(\/|$|\?)/;

function defaultStorage() {
    try {
        return typeof localStorage !== 'undefined' ? localStorage : null;
    } catch (err) {
        return null;
    }
}

function isRestorableRoute(url) {
    if (typeof url !== 'string' || url.length < 2) {
        return false;
    }
    // Hash routes are app-relative: a single leading slash, never a scheme or a host.
    if (url[0] !== '/' || url[1] === '/') {
        return false;
    }
    return !NON_RESTORABLE_ROUTE.test(url);
}

export function rememberNativeRoute(url, {
    win = typeof window !== 'undefined' ? window : undefined,
    storage = defaultStorage(),
    now = Date.now(),
} = {}) {
    if (!storage || !isNativeApp(win) || !isRestorableRoute(url)) {
        return false;
    }
    try {
        storage.setItem(NATIVE_LAST_ROUTE_KEY, JSON.stringify({ url, at: now }));
        return true;
    } catch (err) {
        return false;
    }
}

export function getRememberedNativeRoute({
    win = typeof window !== 'undefined' ? window : undefined,
    storage = defaultStorage(),
    now = Date.now(),
} = {}) {
    if (!storage || !isNativeApp(win)) {
        return null;
    }
    try {
        const raw = storage.getItem(NATIVE_LAST_ROUTE_KEY);
        if (!raw) {
            return null;
        }
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object' || typeof parsed.at !== 'number') {
            return null;
        }
        if (now - parsed.at > NATIVE_LAST_ROUTE_MAX_AGE_MS) {
            storage.removeItem(NATIVE_LAST_ROUTE_KEY);
            return null;
        }
        return isRestorableRoute(parsed.url) ? parsed.url : null;
    } catch (err) {
        return null;
    }
}

// customer.featureFlags.hideNativeAppPurchases on the domain customer (appState.currentCustomer,
// served by /api/customers/public): App Store guideline 3.1.1 and Google Play Billing forbid
// selling digital race access (tracking, results, news, streaming) outside IAP / Play Billing,
// and steering users to an outside shop is not allowed either. With the flag on, the race
// routes replace their purchase flow with RaceSubscribeUnavailableInApp inside the app only;
// access bought on the web is still honoured through user.raceSubscriptions.
export function isNativePurchaseHidden(customer, win) {
    const flag = customer?.featureFlags?.hideNativeAppPurchases;
    const enabled = flag === true || Number(flag) === 1;
    return enabled && isNativeApp(win);
}
